// SSE client for the Attune endpoint (/api/ai/attune/generate). Mirrors
// lib/designer/streamClient.ts: pulls the learner's JWT from the supabase
// session, POSTs a MODE plus that mode's DATA, and forwards what streams back.
//
// The client never sends a prompt, a model, a schema or a token budget — the
// mode selects all of those on the server (lib/attune/prompts.ts), so a
// learner cannot run arbitrary prompts against arbitrary models on their
// tenant's credit balance. What crosses is data: the conversation so far, the
// lesson slice, course placement, the diagnostic, the learner's name.

import { supabase } from '../supabase';
import type { AttuneMode, AttuneModeData } from './prompts';

export interface AttuneMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface AttuneChatResult {
  fullText: string;
  generationId?: string;
  creditsDebited?: number;
  stopReason?: string;
}

export class AttuneStreamError extends Error {
  constructor(public code: string, message: string, public requested?: number) {
    super(message);
    this.name = 'AttuneStreamError';
  }
}

/** The conversation modes — one streamed assistant turn, plain text. */
export type AttuneChatMode = Extract<AttuneMode, 'chat_turn' | 'chat_first_contact' | 'chat_greet' | 'chat_close' | 'reflection'>;
/** The structured modes — a single forced tool call returning JSON. */
export type AttuneGenerateMode = Extract<AttuneMode, 'build' | 'build_repair' | 'arc' | 'diagnose' | 'wrapup'>;

interface AttuneChatOptions<M extends AttuneChatMode> {
  mode: M;
  /** That mode's data shape (AttuneModeData) — the compile-time half of the
   *  contract the server validates at runtime. */
  data: AttuneModeData[M];
  signal?: AbortSignal;
  onText?: (delta: string) => void;
  onDone?: (event: { generationId?: string; creditsDebited?: number; stopReason?: string }) => void;
  onError?: (error: AttuneStreamError) => void;
}

const ENDPOINT = '/api/ai/attune/generate';

async function postAttune(mode: AttuneMode, data: object, signal?: AbortSignal): Promise<Response> {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new AttuneStreamError('unauthorized', 'Not signed in');

  return fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify({ mode, data }),
    signal,
  });
}

async function errorFrom(res: Response): Promise<AttuneStreamError> {
  let errBody: { error?: string; message?: string; requested?: number } | null = null;
  try { errBody = await res.json(); } catch { /* not JSON */ }
  return new AttuneStreamError(
    errBody?.error || `http_${res.status}`,
    errBody?.message || `Request failed (${res.status})`,
    errBody?.requested,
  );
}

export async function streamAttuneChat<M extends AttuneChatMode>(opts: AttuneChatOptions<M>): Promise<AttuneChatResult> {
  const res = await postAttune(opts.mode, opts.data, opts.signal);

  if (!res.ok) {
    const err = await errorFrom(res);
    opts.onError?.(err);
    throw err;
  }
  if (!res.body) throw new AttuneStreamError('no_stream', 'Server returned no stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let generationId: string | undefined;
  let creditsDebited: number | undefined;
  let stopReason: string | undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const evt of events) {
        const line = evt.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;
        let parsed: { type?: string; delta?: string; message?: string; generationId?: string; creditsDebited?: number; stopReason?: string };
        try { parsed = JSON.parse(payload); } catch { continue; }

        if (parsed.type === 'text-delta' && typeof parsed.delta === 'string') {
          fullText += parsed.delta;
          opts.onText?.(parsed.delta);
        } else if (parsed.type === 'done') {
          generationId = parsed.generationId;
          creditsDebited = parsed.creditsDebited;
          stopReason = parsed.stopReason;
        } else if (parsed.type === 'error') {
          const err = new AttuneStreamError('upstream_error', parsed.message || 'Upstream error');
          opts.onError?.(err);
          throw err;
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }

  opts.onDone?.({ generationId, creditsDebited, stopReason });
  return { fullText, generationId, creditsDebited, stopReason };
}

// ─── generate modes (single forced-tool call) ─────────────────────────────
// Used by the Experience end-mode to build a personalised interactive slide.
// Mirrors streamAttuneChat's transport but reads the `tool-call` event and
// returns the assembled tool input (the SlideData the importer consumes).

interface AttuneGenerateOptions<M extends AttuneGenerateMode> {
  mode: M;
  data: AttuneModeData[M];
  signal?: AbortSignal;
}

export interface AttuneGenerateResult {
  data: Record<string, unknown>;
  generationId?: string;
  creditsDebited?: number;
}

export async function streamAttuneGenerate<M extends AttuneGenerateMode>(opts: AttuneGenerateOptions<M>): Promise<AttuneGenerateResult> {
  const res = await postAttune(opts.mode, opts.data, opts.signal);

  if (!res.ok) throw await errorFrom(res);
  if (!res.body) throw new AttuneStreamError('no_stream', 'Server returned no stream');

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let toolArgs: Record<string, unknown> | undefined;
  let argsText = '';
  let generationId: string | undefined;
  let creditsDebited: number | undefined;
  let stopReason: string | undefined;

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const events = buffer.split('\n\n');
      buffer = events.pop() ?? '';

      for (const evt of events) {
        const line = evt.split('\n').find((l) => l.startsWith('data: '));
        if (!line) continue;
        const payload = line.slice(6).trim();
        if (!payload) continue;
        let parsed: {
          type?: string; args?: Record<string, unknown>; argsTextDelta?: string;
          message?: string; generationId?: string; creditsDebited?: number;
          stopReason?: string;
        };
        try { parsed = JSON.parse(payload); } catch { continue; }

        if (parsed.type === 'tool-call' && parsed.args && typeof parsed.args === 'object') {
          toolArgs = parsed.args;
        } else if (parsed.type === 'tool-call-delta' && typeof parsed.argsTextDelta === 'string') {
          argsText += parsed.argsTextDelta;
        } else if (parsed.type === 'done') {
          generationId = parsed.generationId;
          creditsDebited = parsed.creditsDebited;
          stopReason = parsed.stopReason;
        } else if (parsed.type === 'error') {
          throw new AttuneStreamError('upstream_error', parsed.message || 'Upstream error');
        }
      }
    }
  } finally {
    try { reader.releaseLock(); } catch { /* noop */ }
  }

  let data = toolArgs;
  if (!data && argsText) {
    try { data = JSON.parse(argsText) as Record<string, unknown>; } catch { /* leave undefined */ }
  }
  if (!data) {
    // A max-tokens cut leaves a partial tool call that fails the parse above —
    // name the real cause instead of the generic "no structured result".
    if (stopReason === 'length') {
      throw new AttuneStreamError('truncated', 'The build ran out of output space before completing');
    }
    throw new AttuneStreamError('no_tool_call', 'Model did not return a structured result');
  }

  return { data, generationId, creditsDebited };
}
